import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, CheckCircle2, AlertTriangle, FileSpreadsheet } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { OPS } from "@/constants/testIds";
import { fmtNumber, currencySuffix } from "@/lib/format";

function Stat({ testId, label, value, lang, tone }) {
    const color =
        tone === "profit"
            ? "text-[color:var(--success)]"
            : tone === "loss"
            ? "text-[color:var(--danger)]"
            : "text-[color:var(--ink)]";
    return (
        <div data-testid={testId} className="rounded-lg surface p-3 text-center min-w-0">
            <p className={`truncate text-base font-bold tnum ${color}`} title={`${fmtNumber(value, lang, 2)} ${currencySuffix(lang)}`}>
                {fmtNumber(value, lang, 2)}
                <span className="ms-1 text-[10px] font-medium text-[color:var(--ink-muted)]">{currencySuffix(lang)}</span>
            </p>
            <p className="text-[11px] text-[color:var(--ink-muted)]">{label}</p>
        </div>
    );
}

export default function ImportErpPlDialog({ open, onOpenChange, onIngested }) {
    const { t, lang } = useI18n();
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const fileRef = useRef(null);

    useEffect(() => {
        if (open) {
            setFile(null);
            setResult(null);
            setError(null);
        }
    }, [open]);

    const doUpload = async () => {
        if (!file) return;
        setUploading(true);
        setResult(null);
        setError(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/financials/pl-ingest", fd, { timeout: 180000 });
            setResult(r.data);
            toast.success(t("import_done"));
            onIngested && onIngested();
        } catch (e) {
            const msg = e?.response?.data?.detail;
            setError(typeof msg === "string" ? msg : t("error_generic"));
        } finally {
            setUploading(false);
        }
    };

    const totals = result?.totals || {};
    const isProfit = (totals.net ?? 0) >= 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={OPS.erpPl.dialog} className="max-w-2xl max-h-[88vh] overflow-y-auto scrollbar-thin">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileSpreadsheet className="h-5 w-5 text-[color:var(--brand-orange)]" />
                        {t("erp_pl_title")}
                    </DialogTitle>
                    <DialogDescription>{t("erp_pl_desc")}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* File picker */}
                    <div
                        className="rounded-lg border border-dashed border-[color:var(--border-strong)] surface-2 p-4 flex items-center gap-3 cursor-pointer"
                        onClick={() => fileRef.current?.click()}
                    >
                        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[color:rgba(186,217,235,0.5)]">
                            <FileSpreadsheet className="h-5 w-5 text-[color:var(--brand-brown)]" />
                        </span>
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-[color:var(--ink)]">
                                {file ? `${t("selected_file")}: ${file.name}` : t("erp_pl_choose")}
                            </p>
                            <p className="text-xs text-[color:var(--ink-muted)]" dir="ltr">
                                XLS · Profit - Loss By Cost Center
                            </p>
                        </div>
                        <input
                            ref={fileRef}
                            data-testid={OPS.erpPl.file}
                            type="file"
                            accept=".xls,.xlsx,application/vnd.ms-excel"
                            className="hidden"
                            onChange={(e) => {
                                setFile(e.target.files?.[0] || null);
                                setResult(null);
                                setError(null);
                            }}
                        />
                    </div>

                    {/* Rejection error */}
                    {error ? (
                        <div
                            data-testid={OPS.erpPl.error}
                            className="rounded-lg border border-[color:var(--danger)] bg-[color:rgba(220,38,38,0.06)] p-3 flex items-start gap-2 text-xs text-[color:var(--danger)]"
                        >
                            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                            <span className="leading-relaxed">{error}</span>
                        </div>
                    ) : null}

                    {/* Reconciliation summary */}
                    {result ? (
                        <div data-testid={OPS.erpPl.result} className="rounded-lg surface-2 border border-[color:var(--border-soft)] p-3 space-y-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="flex items-center gap-2 text-sm font-semibold text-[color:var(--success)]">
                                    <CheckCircle2 className="h-4 w-4" />
                                    {t("import_done")}
                                </div>
                                <Badge data-testid={OPS.erpPl.month} variant="secondary" className="bg-[color:rgba(186,217,235,0.5)] text-[color:var(--brand-brown)] border-0">
                                    {t("erp_pl_month")}: <bdi dir="ltr" className="ms-1 font-semibold">{result.month}</bdi>
                                </Badge>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                <Stat testId={OPS.erpPl.stat("revenue")} label={t("erp_pl_revenue")} value={totals.revenue} lang={lang} />
                                <Stat testId={OPS.erpPl.stat("expense")} label={t("erp_pl_expense")} value={totals.expense} lang={lang} />
                                <Stat
                                    testId={OPS.erpPl.stat("net")}
                                    label={`${t("erp_pl_net")} — ${isProfit ? t("erp_pl_profit") : t("erp_pl_loss")}`}
                                    value={totals.net}
                                    lang={lang}
                                    tone={isProfit ? "profit" : "loss"}
                                />
                            </div>

                            <p className="text-[11px] text-[color:var(--ink-muted)] flex items-center gap-1.5">
                                <CheckCircle2 className="h-3.5 w-3.5 text-[color:var(--success)] shrink-0" />
                                {t("erp_pl_validation_ok")}
                                <span className="tnum" dir="ltr">
                                    ({result.counts?.lines?.inserted || 0}+ / {result.counts?.lines?.updated || 0}~)
                                </span>
                            </p>

                            {result.unmapped?.length ? (
                                <div
                                    data-testid={OPS.erpPl.unmapped}
                                    className="rounded-lg border border-[color:var(--brand-orange)] bg-[color:rgba(209,128,47,0.08)] p-3 space-y-1.5"
                                >
                                    <p className="flex items-center gap-1.5 text-xs font-semibold text-[color:var(--brand-brown)]">
                                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                        {t("erp_pl_unmapped_title")} ({fmtNumber(result.unmapped.length, lang, 0)})
                                    </p>
                                    <p className="text-[11px] text-[color:var(--ink-muted)]">{t("erp_pl_unmapped_desc")}</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {result.unmapped.map((cc) => (
                                            <Badge key={cc} variant="outline" className="border-[color:var(--brand-orange)] text-[color:var(--brand-brown)] text-[11px]">
                                                {cc}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            ) : null}

                            <div className="space-y-1.5">
                                <p className="text-xs font-semibold text-[color:var(--ink)]">{t("erp_pl_per_cc")}</p>
                                <div className="max-h-[240px] overflow-y-auto scrollbar-thin rounded-lg border border-[color:var(--border-soft)]">
                                    <Table>
                                        <TableHeader className="sticky top-0 z-10">
                                            <TableRow className="bg-[color:rgba(224,222,217,0.92)] hover:bg-[color:rgba(224,222,217,0.92)]">
                                                <TableHead className="text-start whitespace-nowrap">{t("erp_pl_cc")}</TableHead>
                                                <TableHead className="text-start whitespace-nowrap">{t("erp_pl_branch")}</TableHead>
                                                <TableHead className="text-end whitespace-nowrap">{t("erp_pl_revenue")}</TableHead>
                                                <TableHead className="text-end whitespace-nowrap">{t("erp_pl_expense")}</TableHead>
                                                <TableHead className="text-end whitespace-nowrap">{t("erp_pl_net")}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {(result.per_cost_center || []).map((c) => (
                                                <TableRow key={c.cost_center} data-testid={OPS.erpPl.ccRow(c.cost_center)}>
                                                    <TableCell className="font-medium text-[color:var(--ink)] whitespace-nowrap">
                                                        {c.cost_center}
                                                    </TableCell>
                                                    <TableCell className="whitespace-nowrap">
                                                        {c.mapped ? (
                                                            <span className="text-xs text-[color:var(--ink)]">
                                                                {lang === "ar" ? c.branch_name_ar || c.branch_name_en : c.branch_name_en || c.branch_name_ar}
                                                            </span>
                                                        ) : (
                                                            <Badge variant="outline" className="border-[color:var(--brand-orange)] text-[color:var(--brand-brown)] text-[10px]">
                                                                {t("erp_pl_not_mapped")}
                                                            </Badge>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-end tnum text-[color:var(--ink)]">
                                                        {fmtNumber(c.revenue, lang, 2)}
                                                    </TableCell>
                                                    <TableCell className="text-end tnum text-[color:var(--ink)]">
                                                        {fmtNumber(c.expense, lang, 2)}
                                                    </TableCell>
                                                    <TableCell
                                                        className={`text-end tnum font-semibold ${
                                                            c.net >= 0 ? "text-[color:var(--success)]" : "text-[color:var(--danger)]"
                                                        }`}
                                                    >
                                                        {fmtNumber(c.net, lang, 2)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
                        {t("cancel")}
                    </Button>
                    <Button
                        data-testid={OPS.erpPl.upload}
                        onClick={doUpload}
                        disabled={!file || uploading}
                        className="bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white"
                    >
                        {uploading ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Upload className="h-4 w-4 me-2" />}
                        {uploading ? t("uploading") : t("upload")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
